import {
  Api,
  ContentType,
  FullRequestParams,
  HttpClient,
  HttpResponse,
} from '@asset-metrix/portal-gateway-client-js';

import { PORTAL_GATEWAY_API_BASE_URL } from '../config';
import { useStore } from '../store';
import { HttpError } from '../utils/errors';

import { getRequestId } from './tracing';

export const NO_ROLE_ID = 'no-role-id';

export const getPublicApiHeaders = () => {
  const { activeUserRole, sessionId, tenantName } = useStore.getState();
  const roleId = activeUserRole?.id;

  return {
    ...(tenantName != null && { 'X-Tenant-Name': tenantName }),
    ...(roleId != null && { 'X-Role-ID': roleId }),
    'X-Request-ID': getRequestId(),
    'X-Session-ID': sessionId,
  };
};

export function getRequestOptions() {
  const { session } = useStore.getState();
  const authToken = session?.getAuthToken();

  return {
    headers: {
      ...getPublicApiHeaders(),
      authorization: `Bearer ${authToken}`,
    },
  };
}

export function createApi(customFetch?: typeof fetch) {
  const httpClient = new HttpClient({
    baseUrl: PORTAL_GATEWAY_API_BASE_URL.replace(/\/$/, ''),
    securityWorker: () => ({
      headers: getRequestOptions().headers,
    }),
    ...(customFetch && { customFetch }),
  });

  console.debug(
    'Portal gateway client initialized with baseUrl %s',
    PORTAL_GATEWAY_API_BASE_URL.replace(/\/$/, '')
  );

  // Patch contentFormatters to support file arrays for form data, which is currently
  // not supported by swagger-typescript-api
  // c.f. https://github.com/acacode/swagger-typescript-api/issues/776
  const originalFormDataFormatter =
    // @ts-expect-error Patch internal contentFormatters to support arrays in form-data
    httpClient.contentFormatters[ContentType.FormData];

  const patchedFormDataFormatter = (input: Record<string, unknown>) => {
    const formData = originalFormDataFormatter(input) as FormData;
    for (const key of Object.keys(input)) {
      const value = input[key];
      if (Array.isArray(value)) {
        formData.delete(key);
        for (const entry of value) {
          if (!(entry instanceof Blob) && typeof entry !== 'string') {
            throw new TypeError(
              'Only string or Blob values are supported to be used in array for form-data requests'
            );
          }
          formData.append(key, entry);
        }
      }
    }
    return formData;
  };

  // @ts-expect-error Patch internal contentFormatters to support arrays in form-data
  httpClient.contentFormatters[ContentType.FormData] = patchedFormDataFormatter;

  // Monkey patch request method to include token refresh
  const originalRequestMethod = httpClient.request;
  httpClient.request = async function modifiedRequestMethod<
    T = unknown,
    E = unknown,
  >(params: FullRequestParams): Promise<HttpResponse<T, E>> {
    const res = await originalRequestMethod<T, E>(params);

    // An http status code "unauthorized" could be caused by an expired access token. The refresh token might still be
    // valid, so we try a token refresh first
    if (res.status === 401) {
      const { refreshSession, session } = useStore.getState();

      if (session != null) {
        try {
          await refreshSession();
          return await originalRequestMethod<T, E>(params);
        } catch {
          throw new HttpError((await res.text()) ?? res.statusText, res.status);
        }
      }
    }

    return res;
  };
  return new Api(httpClient);
}

export const portalGatewayClient = createApi();
